/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LiveChatContext } from '../../screens/LiveChatScreen';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import data from '@emoji-mart/data';
import languages from '../../assets/ISO-639-1-language.json';

// Component imports
import { Button, Col, Collapse, Divider, Form, Input, Popover, Row, Space, Typography, Upload, Tooltip } from 'antd';
import { AppstoreAddOutlined, MessageOutlined, SearchOutlined, SmileOutlined, TranslationOutlined } from '@ant-design/icons';
import { UploadFileIcon } from '../Icons/UploadFileIcon';
import CustomInputAutomationForm from '../Forms/CustomInputAutomationForm';
import EmojiPicker from '@emoji-mart/react';
import PredefinedEntitiesPanel from './PredefinedEntitiesPanel';
import RichTextEditor from '../Forms/Fields/RichTextEditor/RichTextEditor';
import { EditIcon } from '../Icons/EditIcon';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';

// Misc imports
import { joinConversation, sendConversationMessage, sendTypingEvent } from '../../store/liveChat/liveChatThunks';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useTriggerAutomationMutation } from '../../store/predefinedEntities/predefinedEntitiesApi';
import { useUploadAttachmentsMutation } from '../../store/liveChat/liveChatApi';
import { useCreateTranslationMutation } from '../../store/realTimeTranslation/realTimeTranslation';
import { useFetchLiveChatApplicationSettingsQuery } from '../../store/liveChatSettings/liveChatSettingsApi';
import { CONVERSATION_STATUSES, LIVECHAT_SEND_MESSAGE_MODE } from '../../constants/liveChat';
import { unescapeHtml } from '../../utils/string/unescapeHtml';
import createAutomationBody from '../../utils/misc/createAutomationBody';
import generateGroupedEntities from '../../utils/misc/groupPredefinedEntitiesByCategory';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';
import { EXCLUDED_EMOJI_IDS } from '../../constants/main';

// Constant declarations
let uploadTimeout = 0;
let typingEventTimeout = 0;
let typingEventInterval = 0;
const liveChatInputId = 'hbfLiveChatSendbox';
let translateTimeout = 0;

const INPUT_PANELS = {
  AUTOMATIONS: 'automations',
  RESPONSES: 'responses'
};

const ENTITY_TYPES = {
  AUTOMATION: 'automation',
  RESPONSE: 'response'
};

const LiveChatInput = ({
  canRejoin = false,
  hasJoined = false,
  isInactive = false
}) => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const messageInputRef = useRef();

  const { sendMessageMode, spellCheckLanguage } = useSelector(state => state.livechat);
  const { setIsEditingResponses, setIsEditingAutomations, setSidebarOpen } = useContext(LiveChatContext);
  const { data: automations } = getCurrentQueryState('fetchAutomations', { orgId });
  const { data: activeConversationInfo, isLoading: isFetchingActiveConversationInfo } = getCurrentQueryState('fetchActiveConversation', { orgId, conversationId: searchParams.get('conversation') });
  const { data: predefinedResponses } = getCurrentQueryState('fetchPredefinedResponses', { orgId, urlParams: { tenantScope: activeConversationInfo?.tenantId } });
  const { data: livechatSettings } = useFetchLiveChatApplicationSettingsQuery({ orgId });
  //TODO Update with feature flag and client/agent language check

  // Local state
  const [attachments, setAttachments] = useState([]);
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [filteredResponses, setFilteredResponses] = useState([]);
  const [filteredAutomations, setFilteredAutomations] = useState([]);
  const [isCommandsPanelOpen, setIsCommandsPanelOpen] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [commandToTrigger, setCommandToTrigger] = useState();
  const [customAutomation, setCustomAutomation] = useState(null);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [uploadAttachments, { isLoading: isUploadingFiles }] = useUploadAttachmentsMutation();
  const [triggerAutomation] = useTriggerAutomationMutation();
  const [shouldSendTypingEvent, setShouldSendTypingEvent] = useState(false);
  const [inputPanel, setInputPanelOpen] = useState({ isOpen: false, panel: INPUT_PANELS.AUTOMATIONS });
  const [isSearchingResponses, setIsSearchingResponses] = useState(false);
  const [translate] = useCreateTranslationMutation();
  const groupedEntities = useMemo(() => generateGroupedEntities(filteredEntities), [filteredEntities]);
  let insertTextToInput = null; // Function to insert text in the editor, must be set in the onInsertText prop of the editor. Mostly used to insert emojis from an external picker

  const sendBoxWrapperRef = useRef(null);

  useEffect(() => {
    if (activeConversationInfo) {
      form.resetFields();
      setCommandToTrigger(null);
    }
  }, [activeConversationInfo?.id]);

  useEffect(() => {
    if (automations) {
      setFilteredAutomations(automations?.filter(automation => automation.name.toLowerCase().includes(form.getFieldValue('message').substring(1).toLowerCase())));
    }
  }, [automations]);

  useEffect(() => {
    if (predefinedResponses) {
      setFilteredResponses(predefinedResponses?.filter(automation => automation.name.toLowerCase().includes(form.getFieldValue('message').substring(1).toLowerCase())));
    }
  }, [predefinedResponses]);

  useEffect(() => {
    setFilteredEntities([...filteredAutomations.map((automation) => ({ ...automation, type: ENTITY_TYPES.AUTOMATION })), ...filteredResponses.map(response => ({ ...response, type: ENTITY_TYPES.RESPONSE }))]);
  }, [filteredAutomations, filteredResponses]);

  useEffect(() => {
    if (shouldSendTypingEvent && activeConversationInfo) {
      sendTypingEvent(activeConversationInfo.id);
      typingEventInterval = setInterval(() => {
        sendTypingEvent(activeConversationInfo.id);
      }, 3000);
    } else {
      clearInterval(typingEventInterval);
    }
  }, [shouldSendTypingEvent]);

  useEffect(() => {
    messageInputRef?.current?.focus();
    if (commandToTrigger) {
      setIsCommandsPanelOpen(false);
      if (commandToTrigger?.type === ENTITY_TYPES.AUTOMATION) {
        insertTextToInput(`⏎ ${commandToTrigger.name}`, 0, true);
        //Trigger command after insert
        form.submit();
      }
    }
  }, [commandToTrigger]);

  useEffect(() => {
    if (!isCommandsPanelOpen) {
      setSelectedCommand(null);
    }
  }, [isCommandsPanelOpen]);

  useEffect(() => {
    if (isInactive && form.getFieldValue('message')) {
      form.resetFields();
    }
  }, [isInactive]);

  const onSendMessage = (isOriginalMessage) => {
    if (!hasJoined) {
      return;
    }

    let message;
    let originalText;
    if (form.getFieldValue('translation') && !isOriginalMessage) {
      message = form.getFieldValue('translation');
      originalText = form.getFieldValue('message');
    } else {
      message = form.getFieldValue('message');
    }
    if (commandToTrigger) {
      return commandToTrigger.agentInput && commandToTrigger.agentInput.length
        ? setCustomAutomation(commandToTrigger) : onQuickCommandTrigger();
    }
    if (message) {
      dispatch(sendConversationMessage({
        text: message,
        originalText: originalText,
        livechatConversation: {
          id: activeConversationInfo.id
        },
        languageCode: livechatSettings.agentDefaultLanguage
      }));
    }

    form.resetFields();
    messageInputRef.current?.focus();
  };

  const translateMessage = async () => {
    const message = form.getFieldValue('message');
    if (message !== '') {
      const response = await translate({ orgId, targetLanguage: activeConversationInfo?.platformUserLanguage, queryText: [message] });
      if (response.data) {
        form.setFieldsValue({ translation: unescapeHtml(response.data.result[0]) });
      }
    } else {
      form.setFieldsValue({ translation: '' });
    }
  };

  const onQuickCommandTrigger = (item = commandToTrigger) => {
    setCommandToTrigger();
    form.resetFields();
    onAutomationTrigger(item);
  };

  const onAutomationTrigger = (item) => {
    const { url, headers, customInputForm } = item;
    const data = createAutomationBody(activeConversationInfo, customInputForm);
    triggerAutomation({ callbackUrl: url, callbackHeaders: headers, data: data, conversationId: activeConversationInfo?.id, automation: item }).unwrap();
  };

  const onPredefinedEntityClick = (message) => {
    setInputPanelOpen((prev) => {
      return {
        ...inputPanel,
        isOpen: false
      };
    });

    if (inputPanel.panel === INPUT_PANELS.RESPONSES) {
      insertTextToInput(message, 0, true);
    }

    if (inputPanel.panel === INPUT_PANELS.AUTOMATIONS) {
      setCustomAutomation(message);
    }
  };

  const inputPlaceholderText = () => {
    if (activeConversationInfo) {
      if (isInactive && !canRejoin) {
        return activeConversationInfo.status === CONVERSATION_STATUSES.FINISHED
          ? t('This conversation is finished')
          : `${t('This conversation has been transferred to')} ${activeConversationInfo.livechatParticipants.find(participant => participant.participantType === 'agent' && participant.status === 'active')?.fullName}`;
      }
      if (!hasJoined || canRejoin) {
        return t('Join the conversation in order to be able to send messages');
      }
    }
    return t('Type your message here');
  };

  const renderDropdownContents = () => {
    // Get the width of the livechat input via ref to handle resize
    const livechatInputWidth = sendBoxWrapperRef.current?.clientWidth || 0;

    if (inputPanel?.isOpen || isCommandsPanelOpen) {
      return (
        <div
          className="mx-2 scrollbar-custom scrollbar-custom-nohide overflow-y-auto"
          style={{ width: livechatInputWidth - 60, maxHeight: 300 }}
        >
          {
            inputPanel?.isOpen ? (
              <Fragment>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Typography.Paragraph
                      className="fs-sm text-muted"
                    >
                      {inputPanel.panel === 'responses' ? t('Canned responses') : t('Automations')}
                    </Typography.Paragraph>
                  </Col>

                  <Col>
                    {
                      inputPanel.panel === 'responses' ? (
                        <Button
                          size="small"
                          icon={<SearchOutlined />}
                          className="mr-3"
                          onClick={() => setIsSearchingResponses(true)}
                        />
                      ) : null
                    }

                    <Button
                      type="primary"
                      icon={<EditIcon />}
                      size="small"
                      className="fs-sm"
                      onClick={() => {
                        if (inputPanel.panel === 'responses') {
                          setIsEditingResponses(true);
                          setIsEditingAutomations(false);
                        }

                        if (inputPanel.panel === 'automations') {
                          setIsEditingAutomations(true);
                          setIsEditingResponses(false);
                        }

                        setSidebarOpen(true);
                        setInputPanelOpen((prev) => {
                          return {
                            ...inputPanel,
                            isOpen: false
                          };
                        });

                      }}
                    >
                      {t('Edit')}
                    </Button>
                  </Col>
                </Row>

                <PredefinedEntitiesPanel
                  inputRef={messageInputRef}
                  onPredefinedEntityClick={onPredefinedEntityClick}
                  isInactive={isInactive}
                  hasSearch={isSearchingResponses}
                  conversationId={activeConversationInfo?.tenantId}
                  predefinedEntity={inputPanel.panel}
                  setIsSearchingResponses={setIsSearchingResponses}
                />
              </Fragment>
            ) : (
              <Fragment>
                <Typography.Paragraph
                  type="secondary"
                  className="fs-sm"
                >
                  {t('Automations')} &#183; {t('Press enter to select an automation')}
                </Typography.Paragraph>

                <Collapse
                  ghost
                  className="ant-collapse-p-0"
                  defaultActiveKey={Object.keys(groupedEntities)}
                  expandIcon={({ isActive }) => <ArrowRightIcon rotate={isActive ? 90 : 0} className="text-secondary mr-2" />}
                >
                  {
                    Object.keys(groupedEntities)
                      .filter(category => groupedEntities[category].find(entity => entity.type === ENTITY_TYPES.AUTOMATION))
                      .map(category => (
                        <Collapse.Panel
                          header={<Typography.Text type="secondary">{category}</Typography.Text>}
                          key={category}
                          className="mb-2"
                        >
                          <Space
                            wrap={true}
                            size={4}
                            className="mt-1"
                          >
                            {
                              groupedEntities[category].length ? groupedEntities[category]
                                .filter(entity => entity.type === ENTITY_TYPES.AUTOMATION)
                                .map((automation) => {
                                  // Due to different categories, same ids might occur
                                  const isSelected = selectedCommand?.id === automation.id && selectedCommand?.name === automation.name;
                                  return (
                                    <Button
                                      key={automation.id}
                                      size="small"
                                      className={classNames('mb-1 shadow-soft automation-button', {
                                        'bg-layout': isSelected
                                      })}
                                      type={isSelected ? 'link' : 'text'}
                                      onClick={() => {
                                        setSelectedCommand(automation);
                                        setCommandToTrigger(automation);
                                      }}
                                    >
                                      <Typography.Text
                                        ellipsis
                                        className="text-primary"
                                      >
                                        {automation.name}
                                      </Typography.Text>
                                    </Button>
                                  );
                                }) : (
                                <Typography.Paragraph type="secondary" className="fs-sm text-center">
                                  {t('There are no automations that match this command')}
                                </Typography.Paragraph>
                              )
                            }
                          </Space>
                        </Collapse.Panel>
                      ))
                  }
                </Collapse>

                <Divider className="mb-1 mt-1" />

                <Typography.Paragraph
                  type="secondary"
                  className="fs-sm"
                >
                  {t('Canned Responses')} &#183; {t('Press enter to select a response')}
                </Typography.Paragraph>

                <Collapse
                  ghost
                  className="ant-collapse-p-0"
                  defaultActiveKey={Object.keys(groupedEntities)}
                  expandIcon={({ isActive }) => <ArrowRightIcon rotate={isActive ? 90 : 0} className="text-secondary mr-2" />}
                >
                  {
                    Object.keys(groupedEntities)
                      .filter(category => groupedEntities[category].find(entity => entity.type === ENTITY_TYPES.RESPONSE))
                      .map(category => (
                        <Collapse.Panel
                          header={<Typography.Text type="secondary">{category}</Typography.Text>}
                          key={category}
                          className="mb-2"
                        >
                          <Space
                            wrap={true}
                            size={4}
                            className="mt-1"
                          >
                            {
                              groupedEntities[category].length ? groupedEntities[category]
                                .filter(entity => entity.type === ENTITY_TYPES.RESPONSE)
                                .map((response) => {
                                  const isSelected = selectedCommand?.id === response.id && selectedCommand?.name === response.name;
                                  return (
                                    <Button
                                      key={response.id}
                                      size="small"
                                      type={isSelected ? 'link' : 'text'}
                                      className={classNames('mb-1 shadow-soft response-button', {
                                        'bg-layout': isSelected
                                      })}
                                      onClick={() => {
                                        setSelectedCommand(response);
                                        insertTextToInput(response.text, 0, true);
                                        setIsCommandsPanelOpen(false);
                                        messageInputRef.current?.focus();
                                      }}
                                    >
                                      <Typography.Text
                                        ellipsis
                                        className="text-primary"
                                      >
                                        {response.name}
                                      </Typography.Text>
                                    </Button>
                                  );
                                }) : (
                                <Typography.Paragraph type="secondary" className="fs-sm text-center">
                                  {t('There are no responses that match this command')}
                                </Typography.Paragraph>
                              )
                            }
                          </Space>
                        </Collapse.Panel>
                      ))
                  }
                </Collapse>
              </Fragment>
            )
          }
        </div>
      );
    } else {
      return '';
    }
  };

  return (
    <Popover
      open={inputPanel?.isOpen || isCommandsPanelOpen}
      trigger="click"
      autoFocus
      placement="top"
      destroyPopupOnHide
      showArrow={false}
      onOpenChange={(open) => {
        if (!open) {
          setInputPanelOpen((prev) => {
            return {
              ...inputPanel,
              isOpen: open
            };
          });
          setIsCommandsPanelOpen(false);
        }
      }}
      content={renderDropdownContents()}
    >
      <Form
        className="pt-1 pb-2"
        form={form}
        initialValues={{ message: '', translation: '' }}
        onFinish={onSendMessage}
        onValuesChange={(changedValues, _allValues) => {
          const { message } = changedValues;
          if (message?.[0] === '/') {
            setIsCommandsPanelOpen(true);
            const actions = automations?.filter(automation => automation.name.toLowerCase().includes(message.substring(1).toLowerCase()));
            const responses = predefinedResponses?.filter(response => response.name.toLowerCase().includes(message.substring(1).toLowerCase()));
            const entities = [...actions.map((automation) => ({ ...automation, type: ENTITY_TYPES.AUTOMATION })), ...responses.map(response => ({ ...response, type: ENTITY_TYPES.RESPONSE }))];
            setFilteredEntities(entities);
            setFilteredAutomations(actions);
            setFilteredResponses(responses);
            if (!entities.find(entity => entity.id === selectedCommand?.id)) {
              setSelectedCommand(null);
            }
          } else {
            setIsCommandsPanelOpen(false);
          }
        }}
      >
        <Row
          ref={sendBoxWrapperRef}
          wrap={false}
          align="middle"
        >
          {
            (activeConversationInfo && !isInactive && !hasJoined) || (activeConversationInfo && canRejoin) ? (
              <Col flex="none">
                <Button
                  type="primary"
                  className="ml-3"
                  onClick={() => dispatch(joinConversation(activeConversationInfo.id))}
                >
                  {t('Join Conversation')}
                </Button>
              </Col>
            ) : null
          }

          <Col span={24}>
            <Row justify="space-between">
              <Col flex="auto">
                <Form.Item
                  name="message"
                  noStyle
                >
                  <RichTextEditor
                    id={liveChatInputId}
                    index={liveChatInputId}
                    shouldAutoFocus
                    disableEnterDefaultBehavior={sendMessageMode === LIVECHAT_SEND_MESSAGE_MODE.ENTER}
                    ref={messageInputRef}
                    className={classNames('ql-transparent ql-sm py-2', {
                      'text-primary fw-bold': commandToTrigger
                    })}
                    hideToolbar
                    toolbarActions={[]} // disable toolbar actions for now
                    onInsertText={(insertTextFunc) => insertTextToInput = insertTextFunc}
                    bordered={false}
                    disabled={!activeConversationInfo || isFetchingActiveConversationInfo || isInactive || !hasJoined}
                    placeholder={inputPlaceholderText()}
                    style={{ maxHeight: 140 }}
                    onFocus={() => {
                      if (form.getFieldValue('message')?.[0] === '/') {
                      // Timeout needed to not trigger the popover click event
                        setTimeout(() => {
                          setIsCommandsPanelOpen(true);
                        }, 0);
                      }
                    }} // Opens the window if the user types / and focuses on the input
                    spellCheckLanguage={spellCheckLanguage}
                    onKeyDown={(e) => {
                      const shouldSubmit = e.key === 'Enter'
                  && !e.shiftKey
                  && ((sendMessageMode === LIVECHAT_SEND_MESSAGE_MODE.SEND_BUTTON && (e.ctrlKey || e.metaKey)) || sendMessageMode === LIVECHAT_SEND_MESSAGE_MODE.ENTER);

                      if (shouldSubmit) {
                        e.preventDefault();
                        const message = form.getFieldValue('message');

                        // Replace the zero width space added by RichTextEditor Component
                        // We need this because RTE adds that empty line when the user presses ENTER to send the message
                        form.setFieldsValue({
                          message: message.replace(/\u200B$/, '')
                        });

                        if (selectedCommand?.type === ENTITY_TYPES.RESPONSE && isCommandsPanelOpen) {
                          if (!form.getFieldValue('message').includes('/') && form.getFieldValue('message') === selectedCommand.text) {
                            form.submit();
                            setSelectedCommand(null);
                          } else {
                            insertTextToInput(selectedCommand.text, 0, true);
                            setSelectedCommand(null);
                            setIsCommandsPanelOpen(false);
                          }
                        } else {
                          if (isCommandsPanelOpen && selectedCommand != null) {
                            return setCommandToTrigger(selectedCommand);
                          }
                          if (commandToTrigger) {
                            return commandToTrigger.agentInput && commandToTrigger.agentInput.length
                              ? setCustomAutomation(commandToTrigger) : onQuickCommandTrigger();
                          }

                          if (form.getFieldValue('translation')) {
                            onSendMessage();
                          } else {
                            form.submit();
                          }
                        }
                      } else {
                        if (isCommandsPanelOpen) {
                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            e.preventDefault();

                            const currentIndex = filteredEntities.findIndex(entity => (entity.id === selectedCommand?.id && entity.name === selectedCommand.name));

                            let newIndex;
                            if (e.key === 'ArrowUp') {
                              newIndex = currentIndex === 0 ? filteredEntities.length - 1 : currentIndex - 1;
                            } else {
                              newIndex = currentIndex === filteredEntities.length - 1 ? 0 : currentIndex + 1;
                            }

                            setSelectedCommand(filteredEntities[newIndex]);
                          }
                        }

                        if (commandToTrigger) {
                          e.preventDefault();
                          if (e.key === 'Backspace') {
                            setCommandToTrigger();
                            form.resetFields();
                          }
                        }

                        // Handle the typing event
                        setShouldSendTypingEvent(true);
                        clearTimeout(typingEventTimeout);
                        typingEventTimeout = setTimeout(() => setShouldSendTypingEvent(false), 1000);

                        // Throttle translation request to when user stops typing for a while
                        if (livechatSettings?.isTranslationEnabled && activeConversationInfo?.platformUserLanguage !== livechatSettings?.agentDefaultLanguage ) {
                          if (!form.getFieldValue('message')) {
                            form.setFieldsValue({ translation: '' });
                          }
                          clearTimeout(translateTimeout);
                          translateTimeout = setTimeout(() => {
                            if (form.getFieldValue('message')) {
                              translateMessage();
                            }
                          }, 1000);
                        }
                      }
                    }
                    }
                  />
                </Form.Item>
              </Col>
              <Col>
                <Form.Item
                  noStyle
                  hidden={!livechatSettings?.isTranslationEnabled || activeConversationInfo?.platformUserLanguage === livechatSettings?.agentDefaultLanguage}
                >
                  <Button
                    htmlType="submit"
                    type="link"
                    size="large"
                    disabled={!activeConversationInfo || isInactive || !hasJoined}
                    onClick={() => onSendMessage(true)}
                  >
                    {t('Send original')}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        {
          livechatSettings?.isTranslationEnabled && activeConversationInfo?.platformUserLanguage !== livechatSettings?.agentDefaultLanguage ? (
            <Col>
              <Typography.Text
                className="fs-sm fw-bold text-muted py-2'"
                style={{ marginLeft: 16 }}
              >
                {t('Auto-translated message')}
                <span className="mx-2">
                  &#x2022;
                </span>
                {t('Detected user language') + `: ${languages.codes.find(item => item.code === activeConversationInfo?.platformUserLanguage)?.name || 'unknown'}`}
              </Typography.Text>

              <Row
                wrap={false}
                align="middle"
              >
                <Col flex="auto">
                  <Form.Item
                    shouldUpdate
                    noStyle
                  >
                    {() => (
                      <Form.Item
                        name="translation"
                        noStyle
                      >
                        <Input.TextArea
                          className={classNames('mx-1 fs-base transition-none', {
                            'font-style-initial': form.getFieldValue('translation'),
                            'font-style-italic': !form.getFieldValue('translation')
                          })}
                          bordered={false}
                          autoSize={{ minRows: 0, maxRows: 3 }}
                          placeholder={inputPlaceholderText()}
                          disabled={!activeConversationInfo || isFetchingActiveConversationInfo || isInactive || !hasJoined}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              onSendMessage();
                            }
                          }}
                        />
                      </Form.Item>
                    )}
                  </Form.Item>
                </Col>

                <Col flex="none" className="mr-1">
                  <Form.Item
                    noStyle
                    shouldUpdate
                  >
                    {() => (
                      <Button
                        type="primary"
                        onClick={() => {
                          onSendMessage();
                        }}
                        icon={<TranslationOutlined />}
                        disabled={!activeConversationInfo || isFetchingActiveConversationInfo || isInactive || !hasJoined || !form.getFieldValue('translation')}
                      >
                        {t('Send Translation')}
                      </Button>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          ) : null
        }

        <Divider className="mt-1 mb-2" />

        <Row
          align="middle"
          justify="space-between"
          className="px-2"
        >
          <Space
            wrap={false}
            size={4}
          >
            <Tooltip
              overlayClassName="pointer-events-none"
              title={t('Save time by using pre-written canned responses.')}
            >
              <Button
                type="link"
                className="text-secondary"
                disabled={!activeConversationInfo || !hasJoined || isInactive}
                icon={<MessageOutlined className="fs-xl" />}
                onClick={() => setInputPanelOpen({
                  panel: INPUT_PANELS.RESPONSES,
                  isOpen: true
                })}
              />
            </Tooltip>

            <Tooltip
              overlayClassName="pointer-events-none"
              title={t('Add tickets and automated workflows.')}
            >
              <Button
                type="link"
                className="text-secondary"
                disabled={!activeConversationInfo}
                icon={<AppstoreAddOutlined className="fs-xl" />}
                onClick={() => setInputPanelOpen({
                  panel: INPUT_PANELS.AUTOMATIONS,
                  isOpen: true
                })}
              />
            </Tooltip>

            <Upload
              fileList={attachments}
              beforeUpload={(_file, fileList) => {
                setAttachments(fileList.slice(0, 5));
              }}
              customRequest={() => {
                /*
                * We use a debounce here to trigger the
                * function only once, for a single API call
                */
                clearTimeout(uploadTimeout);
                uploadTimeout = setTimeout(async () => {
                  const formData = new FormData();
                  let totalSize = 0;
                  for (const file of attachments) {
                    const { size } = file;
                    totalSize += size;
                    formData.append('files', file);

                  }
                  // Maximum allowed filesize is 20 MBs
                  if (totalSize > 20 * 1024 * 1024) {
                    openConsoleAlertMessage({ type: 'error', message: t('The maximum allowed upload size is 20 MBs') });
                  } else if (formData.has('files')) {
                    await uploadAttachments({ data: formData, urlParams: {
                      conversation: activeConversationInfo?.id
                    } });
                  }
                  // We clear up the attachments array to allow for another upload
                  setAttachments([]);
                });
              }}
              itemRender={() => null}
              multiple
              maxCount={5}
            >
              <Tooltip
                overlayClassName="pointer-events-none"
                title={t('Share relevant documents or images with your customers.')}
              >
                <Button
                  className="text-secondary"
                  type="link"
                  disabled={!activeConversationInfo || isInactive || !hasJoined}
                  icon={<UploadFileIcon className="fs-xxl"/>}
                  loading={isUploadingFiles}
                />
              </Tooltip>
            </Upload>

            <div className="emoji--wrapper">
              <Tooltip
                overlayClassName="pointer-events-none"
                title={t('Add a touch of personality to your messages with emojis.')}
              >
                <Button
                  className="text-secondary"
                  disabled={!activeConversationInfo || isInactive || !hasJoined}
                  type="link"
                  icon={<SmileOutlined className="fs-xl"/>}
                  onClick={() => setIsEmojiPickerOpen(prevState => !prevState)}
                />
              </Tooltip>

              {
                isEmojiPickerOpen ? (
                  <EmojiPicker
                    data={data}
                    theme="light"
                    exceptEmojis={EXCLUDED_EMOJI_IDS}
                    onClickOutside={(e) => {
                      const isEmoji = e.target.closest('.emoji--wrapper');
                      if (!isEmoji) {
                        setIsEmojiPickerOpen(false);
                      }
                    }}
                    onEmojiSelect={(icon) => {
                      setIsEmojiPickerOpen(false);
                      insertTextToInput(icon.native);
                    }}
                  />
                ) : null
              }
            </div>
          </Space>

          {!livechatSettings?.isTranslationEnabled || activeConversationInfo?.platformUserLanguage === livechatSettings?.agentDefaultLanguage ? (
            <Form.Item
              noStyle
            >
              <Button
                htmlType="submit"
                type="primary"
                size="large"
                disabled={!activeConversationInfo || isInactive || !hasJoined}
                onClick={() => form.submit()}
              >
                {t('Send')}
              </Button>
            </Form.Item>
          ) : null}
        </Row>

        {
          customAutomation ? (
            <CustomInputAutomationForm
              setAutomation={setCustomAutomation}
              automation={customAutomation}
              onTrigger={onQuickCommandTrigger}
            />
          ) : null
        }
      </Form>
    </Popover>
  );
};

LiveChatInput.propTypes = {
  canRejoin: PropTypes.bool,
  hasJoined: PropTypes.bool,
  isInactive: PropTypes.bool
};

export default LiveChatInput;
